import React, { useEffect, useState } from "react";
import { api, fmtError, fmtDateTime, fmtNumber } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ConfirmDialog";
import {
    Database,
    Broom,
    ArrowsClockwise,
    Lightning,
    Warning,
    CheckCircle,
    Wrench,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Maintenance() {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState({}); // {action: bool}
    const confirm = useConfirm();

    const load = async () => {
        try {
            const { data } = await api.get("/maintenance/overview");
            setOverview(data);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runAction = async (key, opts) => {
        const ok = await confirm(opts.confirm);
        if (!ok) return;
        setBusy((b) => ({ ...b, [key]: true }));
        try {
            const { data } = await api.post(opts.path);
            opts.onSuccess?.(data);
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy((b) => ({ ...b, [key]: false }));
        }
    };

    if (loading) return <div className="font-mono text-xs text-slate-400">loading…</div>;
    if (!overview) return <div className="text-sm text-slate-500">No data.</div>;

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Tools</div>
                    <h1 className="font-display text-4xl tracking-tighter">Maintenance</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        Super-admin utilities — purge test data, refresh FX, backfill, manual anomaly scans. Updated {fmtDateTime(overview.generated_at)}.
                    </p>
                </div>
                <Button
                    onClick={load}
                    variant="outline"
                    data-testid="maintenance-refresh"
                    className="rounded-none border-slate-300 gap-2"
                >
                    <ArrowsClockwise size={14} weight="bold" /> Refresh
                </Button>
            </header>

            {/* Integrations status */}
            <section className="bg-white border border-slate-200">
                <div className="px-5 py-3 border-b border-slate-200">
                    <div className="overline">Integrations</div>
                    <h2 className="font-display text-lg">External services</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
                    <IntegrationCard
                        label="Resend email"
                        env="RESEND_API_KEY"
                        active={overview.integrations.resend_email}
                        hint="Notifications + digest emails."
                    />
                    <IntegrationCard
                        label="Twilio WhatsApp"
                        env="TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_WHATSAPP_FROM"
                        active={overview.integrations.twilio_whatsapp}
                        hint="SLA breach + high-severity anomaly alerts."
                    />
                    <IntegrationCard
                        label="Emergent storage"
                        env="EMERGENT_LLM_KEY"
                        active={overview.integrations.emergent_storage}
                        hint="Creative uploads + invoice files."
                    />
                </div>
            </section>

            {/* Quick actions */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                <ActionCard
                    icon={Broom}
                    title="Purge test data"
                    description={`Permanently delete all TEST_-prefixed records across collections. Currently ${fmtNumber(testTotal(overview.test_counts))} TEST_ records present.`}
                    busy={busy["purge"]}
                    testid="maintenance-purge"
                    danger
                    onClick={() =>
                        runAction("purge", {
                            confirm: {
                                title: "Purge all TEST_ data?",
                                description: `This permanently deletes every TEST_-prefixed record across campaigns, clients, templates, platforms, anomalies, reports, invoice requests, creative assets, and test_-prefixed user emails. Production data is untouched.`,
                                confirmText: "Purge TEST_ data",
                                destructive: true,
                            },
                            path: "/maintenance/purge-test-data",
                            onSuccess: (data) => {
                                const total = data?.total || 0;
                                toast.success(`Purged ${fmtNumber(total)} records across ${Object.keys(data?.deleted || {}).length} collections`);
                            },
                        })
                    }
                />
                <ActionCard
                    icon={Database}
                    title="Backfill stage history"
                    description={
                        overview.campaigns_missing_stage_history === 0
                            ? "All campaigns already have stage_history. No-op."
                            : `${fmtNumber(overview.campaigns_missing_stage_history)} legacy campaigns missing stage_history.`
                    }
                    busy={busy["backfill"]}
                    testid="maintenance-backfill"
                    onClick={() =>
                        runAction("backfill", {
                            confirm: {
                                title: "Backfill stage_history?",
                                description: "Adds a single seed entry to any campaign missing stage_history so SLA badges show accurate entered_at. Idempotent — already-backfilled campaigns are skipped.",
                                confirmText: "Run backfill",
                            },
                            path: "/maintenance/backfill-stage-history",
                            onSuccess: (data) => {
                                toast.success(`Backfilled ${fmtNumber(data?.updated || 0)} campaigns`);
                            },
                        })
                    }
                />
                <ActionCard
                    icon={ArrowsClockwise}
                    title="Refresh FX cache"
                    description={
                        overview.fx_cache.entries.length === 0
                            ? "No FX bases cached yet — they fetch lazily on first request."
                            : overview.fx_cache.entries.map((e) => `${e.base}: ${Math.round(e.age_seconds / 60)} min old`).join(" · ")
                    }
                    busy={busy["fx"]}
                    testid="maintenance-fx"
                    onClick={() =>
                        runAction("fx", {
                            confirm: {
                                title: "Refresh FX cache?",
                                description: "Clears the in-memory rate cache and re-fetches from the live provider. Useful if a rate looks stale.",
                                confirmText: "Refresh now",
                            },
                            path: "/maintenance/fx-refresh",
                            onSuccess: (data) => {
                                const ok = (data?.refreshed || []).filter((r) => !r.error).length;
                                toast.success(`Refreshed ${ok} FX base${ok === 1 ? "" : "s"}`);
                            },
                        })
                    }
                />
                <ActionCard
                    icon={Lightning}
                    title="Run anomaly scan"
                    description={`${overview.open_anomalies} currently open. Scan re-evaluates every active campaign and refreshes recommendations.`}
                    busy={busy["scan"]}
                    testid="maintenance-scan"
                    onClick={() =>
                        runAction("scan", {
                            confirm: {
                                title: "Run anomaly scan now?",
                                description: "Re-evaluates pace, week-over-week, missing-EOD and SLA-breach rules across every active campaign. New anomalies fan out an in-app notification.",
                                confirmText: "Run scan",
                            },
                            path: "/anomalies/scan",
                            onSuccess: (data) => {
                                toast.success(`Scan complete · ${data?.new_count || 0} new · ${data?.open_count || 0} open`);
                            },
                        })
                    }
                />
            </section>

            {/* Collection counts */}
            <section className="bg-white border border-slate-200">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                    <Wrench size={16} className="text-slate-500" />
                    <div>
                        <div className="overline">Tenant data</div>
                        <h2 className="font-display text-lg">Collection counts</h2>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-slate-200">
                    {Object.entries(overview.counts).map(([name, count]) => {
                        const testCount = overview.test_counts?.[name];
                        return (
                            <div key={name} className="bg-white p-4" data-testid={`maintenance-count-${name}`}>
                                <div className="overline truncate" style={{ fontSize: 9 }}>{name}</div>
                                <div className="font-mono text-2xl mt-1">{fmtNumber(count)}</div>
                                {testCount > 0 && (
                                    <div className="text-[10px] font-mono text-amber-700 mt-0.5">{testCount} TEST_</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function testTotal(testCounts) {
    return Object.values(testCounts || {}).reduce((sum, n) => sum + (n || 0), 0);
}

function IntegrationCard({ label, env, active, hint }) {
    const Icon = active ? CheckCircle : Warning;
    return (
        <div className="bg-white p-4" data-testid={`maintenance-integration-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} weight={active ? "fill" : "regular"} className={active ? "text-green-700" : "text-amber-700"} />
                <span className="font-medium text-sm">{label}</span>
                <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${active ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                    {active ? "wired" : "stubbed"}
                </span>
            </div>
            <p className="text-xs text-slate-500">{hint}</p>
            <p className="text-[10px] font-mono text-slate-400 mt-1">{env}</p>
        </div>
    );
}

function ActionCard({ icon: Icon, title, description, busy, testid, danger, onClick }) {
    return (
        <div className="bg-white p-5 flex flex-col gap-3" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${danger ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                    <Icon size={18} weight="bold" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="font-display text-lg">{title}</h3>
                    <p className="text-sm text-slate-600 mt-0.5">{description}</p>
                </div>
            </div>
            <div className="flex justify-end">
                <Button
                    onClick={onClick}
                    disabled={busy}
                    data-testid={`${testid}-button`}
                    className={`rounded-none text-white ${danger ? "bg-red-700 hover:bg-red-800" : "bg-slate-900 hover:bg-slate-800"}`}
                >
                    {busy ? "Working…" : "Run"}
                </Button>
            </div>
        </div>
    );
}
